// src/_designereyes/partials/widgets/_new/cards/vendors/distribution/AccountPerformanceTable.tsx
/* AccountPerformanceTable.tsx
   — simplified PrimeReact <DataTable> with built‑in paging & sorting — */

   import React, { FC, useState, useMemo } from 'react'
   import { DataTable } from 'primereact/datatable'
   import { Column }   from 'primereact/column'
   import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
   import {
     faMagnifyingGlass,
     faMagnifyingGlassPlus,
   } from '@fortawesome/free-solid-svg-icons'
   
   import {
     caribbeanDistributors,
     israelDistributors,
     Distributor,
   } from './distributor-data'
   import { useAccount } from '../../../../../widgets'
   
   type Props = { country: 'Caribbean' | 'Israel' }
   
   export const AccountPerformanceTable: FC<Props> = ({ country }) => {
     /* pick the right data set for the tab we’re on */
     const data = useMemo<Distributor[]>(
       () => (country === 'Caribbean' ? caribbeanDistributors : israelDistributors),
       [country]
     )
   
     /* we only care about paging; the table can keep track of sorting */
     const [first, setFirst] = useState(0)
     const [rows, setRows]   = useState(10)
   
     const { handleSelectAccount } = useAccount()
     const [hovered, setHovered] = useState<string | null>(null)
   
     /* tooltip helper ---------------------------------------------------- */
     const distTooltip = (d: Distributor) =>
       `Open Orders #: ${d.OpenOrders}
   Open Order $ : $${d.TotalOpenOrderValue.toLocaleString()}
   Units        : ${d.Units.toLocaleString()}
   Balance Due  : $${d.BalanceDue.toLocaleString()}`
   
     /* action cell (magnifying‑glass) ------------------------------------ */
     const actionBody = (row: Distributor) => (
       <span
         title={distTooltip(row)}
         style={{ cursor: 'pointer' }}
         onClick={() => handleSelectAccount(row.AccountID)}
         onMouseEnter={() => setHovered(row.AccountID)}
         onMouseLeave={() => setHovered(null)}
       >
         <FontAwesomeIcon
           icon={
             hovered === row.AccountID
               ? faMagnifyingGlassPlus
               : faMagnifyingGlass
           }
           style={hovered === row.AccountID ? { color: 'red' } : undefined}
         />
       </span>
     )
   
     /* render helpers for money / units ---------------------------------- */
     const money = (n: number) => `$${n.toLocaleString()}`
     const units = (n: number) => n.toLocaleString()
   
     /* ------------------------------------------------------------------- */
     return (
       <div className='card d-flex flex-column p-4' style={{ minHeight: '45.5rem' }}>
         <div className='d-flex fs-1 fw-bolder px-0 mb-4'>
           Distributor&nbsp;Accounts
         </div>
   
         <DataTable
           value={data}
           paginator
           first={first}
           rows={rows}
           stripedRows
           rowHover
           
           onRowClick={e => handleSelectAccount((e.data as Distributor).AccountID)}
           onPage={e => {                          // <-- no explicit event type
             setFirst(e.first)
             setRows(e.rows)
           }}
           showGridlines
           sortMode='single'       /* built‑in column sorting */
           size='small'
         >
           <Column
             header=''
             body={actionBody}
             style={{ width: '3rem', textAlign: 'center' }}
           />
           <Column field='Name' header='Name' sortable />
           <Column field='ContactInfo' header='Contact Info' sortable style={{ textAlign: 'center' }} />
           <Column field='Region' header='Region' sortable style={{ textAlign: 'center' }} />
           <Column field='BusinessName' header='Business Name' sortable style={{ textAlign: 'center' }} />
           <Column
             field='TotalOpenOrderValue'
             header='Total Open Order $'
             sortable
             body={row => money(row.TotalOpenOrderValue)}
             style={{ textAlign: 'center' }}
           />
           <Column
             field='Units'
             header='# of Units'
             sortable
             body={row => units(row.Units)}
             style={{ textAlign: 'center' }}
           />
           <Column
             field='BalanceDue'
             header='Balance Due'
             sortable
             body={row => money(row.BalanceDue)}
             style={{ textAlign: 'center' }}
           />
         </DataTable>
       </div>
     )
   }
   